import React from 'react';
import type { IntlShape } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Text, Stack } from '@atlaskit/primitives';
import Select, { components } from '@atlaskit/select';
import type {
	FormatOptionLabelMeta,
	StylesConfig,
	ValueType,
	SingleValueProps,
} from '@atlaskit/select';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Field } from '@atlaskit/form';

const i18n = defineMessages({
	audioToneLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.tone-label',
		defaultMessage: 'How does the audio sound?',
		description: 'Label for a Dropdown component used for selecting audio tone',
	},
	professionalToneLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.tone.professional',
		defaultMessage: 'Professional',
		description: 'Label for a Professional tone option in a Select component',
	},
	professionalToneDescription: {
		id: 'audio.audio-briefing-popup.customize-tab.tone.professional.description',
		defaultMessage: 'Clear and direct',
		description: 'Description for the Professional tone option',
	},
	conversationalToneLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.tone.conversational',
		defaultMessage: 'Conversational',
		description: 'Label for a Conversational tone option in a Select component',
	},
	conversationalToneDescription: {
		id: 'audio.audio-briefing-popup.customize-tab.tone.conversational.description',
		defaultMessage: 'Approachable and informative',
		description: 'Description for the Conversational tone option',
	},
	playfulToneLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.tone.playful',
		defaultMessage: 'Playful',
		description: 'Label for a Playful tone option in a Select component',
	},
	playfulToneDescription: {
		id: 'audio.audio-briefing-popup.customize-tab.tone.playful.description',
		defaultMessage: 'Lively and occasionally humorous',
		description: 'Description for the Playful tone option',
	},
	surpriseToneLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.tone.surprise',
		defaultMessage: 'Surprise me',
		description: 'Label for a Surprise Me tone option in a Select component',
	},
	surpriseToneDescription: {
		id: 'audio.audio-briefing-popup.customize-tab.tone.surprise.description',
		defaultMessage: 'Takes on a unique personality',
		description: 'Description for the Surprise Me tone option',
	},
});

type ToneOption = {
	label: string;
	value: string;
	description: string;
};

type ToneOptionProps = {
	option: ToneOption;
	meta: FormatOptionLabelMeta<ToneOption>;
	formatMessage: IntlShape['formatMessage'];
};

const styles = cssMap({
	wrap: {
		whiteSpace: 'break-spaces',
	},
});

const toneSelectStyles: StylesConfig<ToneOption, false> = {
	control: (style) => ({
		...style,
		borderColor: token('color.border'),
	}),
};

// Labels are purposely string values rather than Message objects
// This allows us to call formatMessage where needed
const toneOptions: ToneOption[] = [
	{
		label: 'professionalToneLabel',
		value: 'professionalToneLabel',
		description: 'professionalToneDescription',
	},
	{
		label: 'conversationalToneLabel',
		value: 'conversationalToneLabel',
		description: 'conversationalToneDescription',
	},
	{
		label: 'playfulToneLabel',
		value: 'playfulToneLabel',
		description: 'playfulToneDescription',
	},
	{
		label: 'surpriseToneLabel',
		value: 'surpriseToneLabel',
		description: 'surpriseToneDescription',
	},
];

const ToneOptionComponent = ({ option, meta: { context }, formatMessage }: ToneOptionProps) => {
	if (context === 'menu') {
		return (
			<Stack>
				<Text color="color.text.subtle" weight="semibold">
					{formatMessage(i18n[option.label])}
				</Text>
				<Text color="color.text.subtlest">{formatMessage(i18n[option.description])}</Text>
			</Stack>
		);
	}
	return option.label;
};

const ToneValue = ({ children, data, ...props }: SingleValueProps<ToneOption, false>) => {
	const { formatMessage } = useIntl();

	return (
		<components.SingleValue data={data} {...props}>
			<Box paddingBlock="space.050" paddingInlineEnd="space.075">
				<Box>
					<Text color="color.text.subtle" weight="semibold">
						{formatMessage(i18n[data.label])}
					</Text>
				</Box>
				<Box xcss={styles.wrap}>
					<Text color="color.text.subtlest">{formatMessage(i18n[data.description])}</Text>
				</Box>
			</Box>
		</components.SingleValue>
	);
};

export const ToneSelector = () => {
	const { formatMessage } = useIntl();

	return (
		<Field<ValueType<ToneOption, false>>
			name="audio-tone"
			label={formatMessage(i18n.audioToneLabel)}
			defaultValue={toneOptions[1]}
		>
			{({ fieldProps: { id, ...rest } }) => (
				<Select
					inputId={id}
					{...rest}
					formatOptionLabel={(option, meta) => (
						<ToneOptionComponent option={option} meta={meta} formatMessage={formatMessage} />
					)}
					options={toneOptions}
					components={{
						SingleValue: ToneValue,
					}}
					isSearchable={false}
					menuShouldScrollIntoView
					// Select is a unique case where this is semantically correct
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					styles={toneSelectStyles}
				/>
			)}
		</Field>
	);
};
